import { Component, h, Prop } from '@stencil/core';
import { postItemDetails } from '../../api';
import {
  BookingDetails,
  Cart,
  ItemDetails,
  PriceOptions,
  TimeSlotPicker,
  UnitConfig,
  Widget,
} from '../../redux/types';
import { Unsubscribe } from '@stencil/redux';
import { store } from '../../redux/store';
import { DetailsMode } from './utils/types';
import { requireInit } from '../../redux/actions';
import { makeTimePicker } from './utils/picker';
import { CartStatus, EnabledDisabled } from '../../types/types';
import { RootState } from '../../redux/reducers';
import { buildDropOffDateTimeForApi, buildPickUpDateTimeForApi } from '../../utils/utils';

@Component({
  tag: 'rtr-article-details-engine',
  shadow: false,
})
export class RtrArticleDetailsEngine {
  @Prop() itemId: string;
  @Prop() mode: DetailsMode;
  @Prop() onCloseButtonClick: () => void;
  @Prop() scrollToCalendar: boolean;

  @Prop({ mutable: true }) bookingDetails: BookingDetails;
  @Prop({ mutable: true }) currency: string;
  @Prop({ mutable: true }) currencyPosition: string;
  @Prop({ mutable: true }) widget: Widget;
  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) timePicker: TimeSlotPicker;
  @Prop({ mutable: true }) priceOptions: PriceOptions;
  @Prop({ mutable: true }) itemDetails: ItemDetails;
  @Prop({ mutable: true }) bookingExpired: boolean;
  @Prop({ mutable: true }) loadedComponents: { [name: string]: boolean };
  @Prop({ mutable: true }) showLocation: EnabledDisabled;
  @Prop({ mutable: true }) cart: Cart;
  @Prop({ mutable: true }) unitConfig: UnitConfig;

  unsubscribe!: Unsubscribe;
  requireInit!: typeof requireInit;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const bookingDetails = state.bookingDetails

      let dateRange = null
      if (bookingDetails.fromDate && bookingDetails.toDate) {
        dateRange = {
          from: buildPickUpDateTimeForApi(bookingDetails, true),
          to: buildDropOffDateTimeForApi(bookingDetails, true),
        }
      }

      const withTime = dateRange && bookingDetails && !!bookingDetails.fromTime

      postItemDetails(
        this.itemId,
        bookingDetails.orderDetails?.id,
        state.app.locale,
        dateRange,
        withTime,
        data => (this.itemDetails = data),
        undefined
      );

      return {
        cart: state.cart,
        bookingExpired: state.app.bookingExpired,
        bookingDetails: state.bookingDetails,
        currency: state.system.currency,
        currencyPosition: state.system.pricing.format.currencyPosition,
        widget: state.widget,
        locale: state.app.locale,
        timePicker: makeTimePicker(state?.widget?.timePicker),
        loadedComponents: state.app.loadedComponents,
        showLocation: state.system.widgetConfig.advanced.articleDetails.showLocation,
        unitConfig: state.system.unitsConfig,
        priceOptions: {
          currency: state.system.currency,
          decimalSeparator: state.system.pricing?.format.decimalSeparator,
          currencyPosition: state.system.pricing?.format.currencyPosition,
          displayNet: state.system.pricing?.priceDisplay === 'net',
          priceCalculation: state.system.pricing?.itemPriceCalculation,
        },
      };
    });

    store.mapDispatchToProps(this, {
      requireInit,
    });

    this.requireInit();
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  get cartStatus(): CartStatus {
    if (this.bookingExpired) {
      return CartStatus.Expired;
    } else if (this.bookingDetails.orderDetails) {
      return CartStatus.Active;
    } else {
      return CartStatus.Empty;
    }
  }

  render() {
    if (!this.widget || !this.locale || !this.itemDetails) {
      return null;
    }

    return (
      <rtr-article-details-state
        itemId={this.itemId}
        itemDetails={this.itemDetails}
        locale={this.locale}
        mode={this.mode}
        currency={this.currency}
        currencyPosition={this.currencyPosition}
        widget={this.widget}
        timePicker={this.timePicker}
        priceOptions={this.priceOptions}
        onCloseButtonClick={this.onCloseButtonClick}
        bookingDetails={this.bookingDetails}
        cartStatus={this.cartStatus}
        loadedComponents={this.loadedComponents}
        scrollToCalendar={this.scrollToCalendar}
        showLocation={this.showLocation}
      />
    );
  }
}
